import * as React from 'react';
import { ComponentProps, useMemo } from 'react';
import classnames from '../../../../core/utils/classnames.tsx';
import { useAssistanceFieldContext } from '../AssistanceFieldContext';
import StringField from '../../../../core/components/Fields/StringField';
import useReselectEventListener, { ReselectEvent } from '../useReselectEventListener';
import { fieldIsGreen, fieldIsRed, fieldIsYellow } from '../utils';

export interface FieldProps {
    disabled?: boolean;
    readOnly?: boolean;
    placeholder: string;
    value: any;
    onValueChange: (value: any, updatePayload?: any) => void;
    onUpdatePayloadChange?: (updatePayload: any) => void; // this is needed for lookup fields
    onCommit?: (value: any, updatePayload?: any) => void; // fired by the field when input is finalized
    onFocus: (e: any) => void;
    onBlur: (e: any) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    onReselect: (e: ReselectEvent) => void;
    onReset?: () => void;

    inputRef: any;
    controls: React.ReactNode;

    // maybe move to some context
    confidence: number;
    severity: string;
    documentLoading: boolean;
    documentReadOnly: boolean;
}

export const useFieldProps = <T extends React.ElementType>({
    disabled,
    readOnly,
    placeholder,
    value,
    onValueChange,
    onCommit,
    onFocus,
    onBlur,
    onKeyDown,
    inputRef,
    controls,
    confidence,
    severity,
    documentLoading,
    documentReadOnly,
}: FieldProps): ComponentProps<T> => {
    const { isUpdated, isInvalid, isRequired, isUpdateActive, isReselectActive, isFocusActive, data, config } =
        useAssistanceFieldContext();

    const isGreen = useMemo(() => fieldIsGreen({ config, value, data }), [config, value, data]);
    const isYellow = useMemo(() => fieldIsYellow({ config, value, data }), [config, value, data]);
    const isRed = useMemo(() => fieldIsRed({ config, value, data }), [config, value, data]);

    const showErrorColors = !isFocusActive && !isUpdateActive && !isReselectActive;

    return {
        className: classnames(
            'shadow-none',
            isUpdated &&
                'border-line-success-strong bg-surface-success-weak hover:border-line-success-strong focus-within:border-line-success-strong focus-within:outline-line-success-weak',
            showErrorColors &&
                isInvalid &&
                'border-line-danger-strong bg-surface-danger-weak hover:border-line-danger-strong focus-within:border-line-danger-strong focus-within:outline-line-danger-weak',

            showErrorColors &&
                isGreen &&
                'border-line-success-default hover:border-line-success-default focus-within:border-line-success-default focus-within:outline-line-success-weak',
            showErrorColors &&
                isYellow &&
                'border-line-warning-default hover:border-line-warning-default focus-within:border-line-warning-default focus-within:outline-line-warning-weak',
            showErrorColors &&
                isRed &&
                'border-line-danger-default hover:border-line-danger-default focus-within:border-line-danger-default focus-within:outline-line-danger-weak'
        ),
        placeholder,
        readOnly: readOnly || documentReadOnly || documentLoading || isUpdateActive || isReselectActive,
        disabled: disabled || documentReadOnly,
        required: isRequired,
        value,
        onValueChange,
        onCommit,
        onFocus,
        onBlur,
        onKeyDown,
        inputRef,
        controls,
    } as ComponentProps<T>;
};

export const Field = (props: FieldProps) => {
    /*
    NOTE: This is just a sample field component, the actual field component will be determined
    dynamically by using DynamicField component in the parent component.
    */

    const fieldProps = useFieldProps<typeof StringField>(props);

    useReselectEventListener(props.inputRef, props.onReselect);

    return <StringField {...fieldProps} />;
};

export default Field;
